import styled, { keyframes } from 'styled-components';
import { themeGet } from '@styled-system/theme-get';

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  @media only screen and (min-width: ${themeGet('breakpoints.xlg')}) {
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
  }

  &.layout-column {
    flex-direction: column;
  }
`;

export const Copy = styled.div`
  width: 100%;

  h2,
  h3 {
    margin-bottom: 10px;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    h2,
    h3 {
      margin-bottom: 20px;
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xlg')}) {
    max-width: 410px;

    ${Wrapper}.layout-column & {
      max-width: none;
      margin-bottom: 3rem;
    }
  }
`;

export const PointsBlock = styled.div`
  display: inline-flex;
  flex-direction: column;
  margin: 20px 0 0;

  /* This is the "Points do not expire!" */
  p {
    font-weight: ${themeGet('fontWeights.bold')};
    color: ${themeGet('colors.pc-red', '#E1251B')};
    font-size: 14px;
    line-height: 1.29;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    margin: 40px auto 0;
    align-items: center;
    p {
      font-size: 20px;
      line-height: 1.2;
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xlg')}) {
    margin: 0 auto;
    p {
      font-size: 24px;
      line-height: 1.33;
    }
  }
`;

const spin = keyframes`
  0% {
    top: 0em;
  }
  50% {
    top: -5em;
  }
  100% {
    top: -9em;
  }
`;

export const Spinner = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  width: 100%;
  font-size: 28px;

  span {
    &.roller-text {
      color: ${themeGet('colors.pc-red', '#E1251B')};
      font-weight: ${themeGet('fontWeights.bold')};
      font-size: inherit;
      line-height: 1.25;
      letter-spacing: -0.18px;
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.sm')}) {
    font-size: 32px;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    font-size: 64px;
    span {
      &.roller-text {
        line-height: 1.13;
        letter-spacing: -0.37px;
      }
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xlg')}) {
    font-size: 72px;
    span {
      &.roller-text {
        line-height: 2em;
        letter-spacing: -0.3px;

        @-moz-document url-prefix() {
          line-height: 0.89;
        }
      }
    }
  }
`;

// * Issue Sizing the width on the cells. We want the width to be as small as possibile, so width: min-content, however the size of the 0 is the largest, and the size of 1 is the smallest, and since theyre both kept within the same tag, the min-content is the width of the 0 and makes the 1 take up the width of 0, which is too much
export const NumberCell = styled.div`
  height: 1em;
  width: min-content;
  overflow: hidden;
  line-height: 0em;
  display: inline-block;
  padding: 0 0.5px;

  &.comma {
    line-height: 1em;
  }

  &:first-child {
    span {
      @-moz-document url-prefix() {
        line-height: 1.3em;
      }
    }
  }
`;

export const Number = styled.span`
  position: relative;
  font-size: 1em;
  line-height: 1em;
  color: ${themeGet('colors.pc-red', '#E1251B')};
  font-weight: ${themeGet('fontWeights.bold')};
  font-variant-numeric: tabular-nums;

  @-moz-document url-prefix() {
    line-height: 1.015em;
  }

  /* -4em means 5 will be the number */
  top: ${({ offset }) => (offset ? `-${offset}em` : '')};

  &.animate {
    /* animation-name, animation-duration, animation-timing-function, animation-delay */
    animation: ${spin} 1.05s cubic-bezier(0, 0.43, 0.83, 1.29)
      ${({ length, delay }) => (delay ? length / 2 - delay : 5)};
  }

  ${NumberCell}.comma & {
    font-size: ${({ intl }) => (intl === 'en' ? '1em' : '0.5em')};
  }

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    /* IE support */
    width: 0.59em;
    display: inline-flex;
    text-align: center;
    letter-spacing: 0;
    /* flex-direction: column; */
    /* Making this a flex row fixes the issue in IE, not sure why though */
    /* If the flex-direction is set to column, IE treats it as a flex row */
    /* Setting the direction to row causes IE to treat it as a flex column */
    flex-direction: row;
    justify-content: center;
    align-items: center;

    ${NumberCell}.comma & {
      width: auto;
    }
  }

  @supports not (width: min-content) {
    /* Older Chrome / Firefox / Safari Support */
    width: 0.59em;
    display: inline-flex;
    text-align: center;
    letter-spacing: 0;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    ${NumberCell}.comma & {
      width: auto;
    }
  }

  @supports not (font-variant-numeric: tabular-nums) {
    /* Edge Support */
    width: 0.59em;
    display: inline-flex;
    text-align: center;
    letter-spacing: 0;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    ${NumberCell}.comma & {
      width: auto;
    }
  }
`;

export const Value = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  &::before {
    content: '$';
    color: ${themeGet('colors.pc-red', '#E1251B')};
    font-weight: ${themeGet('fontWeights.bold')};
    font-size: 1em;
    line-height: 0.8em;
    position: relative;
    top: 0.4rem;
    display: ${({ intl }) => (intl === 'en' ? 'inline-block' : 'none')};

    @-moz-document url-prefix() {
      line-height: 1.15em;
    }
  }

  &::after {
    content: '$';
    margin-left: 0.25em;
    color: ${themeGet('colors.pc-red', '#E1251B')};
    font-weight: ${themeGet('fontWeights.bold')};
    font-size: 1em;
    line-height: 0.8em;
    display: ${({ intl }) => (intl === 'fr' ? 'inline-block' : 'none')};

    @-moz-document url-prefix() {
      line-height: 1.15em;
    }
  }
`;
